import { useMemo, useRef, useEffect } from 'react'
import type {
    MarkingEditorState,
    ExtendedMarkingFormData,
} from '../types'
import { getInitialState } from '../utils/state/stateUtils'

/**
 * Параметры для инициализации состояния редактора маркировки
 */
export interface UseMarkingInitializationParams {
    /**
     * Начальные данные из пропсов
     */
    initialData?: Partial<MarkingEditorState>
    /**
     * Сохраненные данные из БД
     */
    savedData?: Partial<ExtendedMarkingFormData>
    /**
     * Сохраненные данные из localStorage (временное решение)
     */
    localData?: Partial<MarkingEditorState>
}


/**
 * Хук для инициализации состояния редактора маркировки
 * Вычисляет начальное состояние на основе переданных данных
 * и обновляет его при изменении входных данных
 */
export function useMarkingInitialization(
    params: UseMarkingInitializationParams
): MarkingEditorState {
    const { initialData, savedData, localData } = params

    // Используем ref для хранения начального состояния, чтобы избежать пересчета
    const initialStateRef = useRef<MarkingEditorState>(
        getInitialState({ initialData, savedData, localData })
    )

    // Отслеживаем предыдущие значения для определения изменений
    const prevSavedDataRef = useRef(savedData)
    const prevLocalDataRef = useRef(localData)
    const prevInitialDataRef = useRef(initialData)

    // Обновляем ref начального состояния только когда данные действительно изменились
    useEffect(() => {
        const savedDataChanged =
            savedData !== undefined && prevSavedDataRef.current !== savedData
        const localDataChanged =
            localData !== undefined && prevLocalDataRef.current !== localData
        const initialDataChanged = prevInitialDataRef.current !== initialData

        if (savedDataChanged || localDataChanged || initialDataChanged) {
            const newInitialState = getInitialState({
                initialData,
                savedData,
                localData,
            })
            initialStateRef.current = newInitialState
            prevSavedDataRef.current = savedData
            prevLocalDataRef.current = localData
            prevInitialDataRef.current = initialData
        }
    }, [savedData, localData, initialData])

    // Вычисляем начальное состояние на основе текущих данных
    // Используем useMemo с зависимостями, чтобы обновляться при загрузке данных
    const initialState = useMemo(() => {
        return getInitialState({
            initialData,
            savedData,
            localData,
        })
    }, [savedData, localData, initialData])

    return initialState
}
